import { AccountService } from './../../pages/account/account.service';
import { Injectable, OnDestroy } from '@angular/core';
import { ApiService } from '../apiservice/apiservice.service';
import { StorageService } from '../storage/storage.service';
import { ConfigService } from '../config/config.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LOGIN_STATE, SharedService } from '../../services/shared/shared.service';
import { BrowseSearchData } from '../../pages/browse/browse.component';
import {
  SearchRequestPayload,
  SearchResults,
  SearchIndexes,
  TargetGroups,
  AvailableTargetsInfo,
  SearchStatus,
  TypeAhead,
  PollResultsRequestPayload,
  SearchTarget,
  Document,
  SearchQuery,
} from '../../core/interfaces/search.interface';
import { Definitions } from '../../core/definitions';
import { RoutingService } from '../routing/routing.service';
import { SearchHistoryService } from '../../components/side-nav/search-history/search-history.service';
import { KidsCatalogSearchService } from '../../pages/kids-catalog-search/kids-catalog-search.service';
import { WebSocketHubConnection, WebSocketService } from '../websocket/websocket.service';
import { ApiResponse } from '../api-response.interface';

export enum SEARCH_TYPE {
  SIMPLE = 1,
  ADVANCED = 2,
  BROWSE = 3,
  LINKED = 4, // not sure waht this is but it exists as a value in backend
  KIDS_CATALOG = 5,
}

// ui only, can be moved
export enum SEARCH_VIEW {
  GALLERY = 'gallery',
  LIST = 'list',
}

export enum CHECKED_STATUS {
  ALL,
  PARTIAL,
  NONE,
}

export enum RESULTTYPE {
  /** Title & Author */
  CLUSTERED = 'Clustered',
  /** Title, Author, Publisher, Date */
  MERGED = 'Merged',
  NONE = 'None',
}

export enum TARGETTYPE {
  /** Union Catalog / A-G Database */
  AG = 'AG',
  /**  */
  AV = 'AV',
  /** Virtual Library Z39.50 */
  ZZ = 'ZZ',
  /** OCLC WorldCat */
  WM = 'WM',
  RB = 'RB',
  ZN = 'ZN',
  /** Overdrive */
  OD = 'OD',
  /** Web Link */
  WL = 'WL',
  /** Web Service */
  WS = 'WS',
  EH = 'EH',
  CG = 'CG',
  PQ = 'PQ',
  /** Hoopla */
  HP = 'HP',
  /** Axis360 */
  BX = 'BX',
}

export enum SORTBYTYPES {
  TITLE_ASCENDING = 'title_asc',
  TITLE_DESCENDING = 'title_desc',
  AUTHOR_ASCENDING = 'author_asc',
  AUTHOR_DESCENDING = 'author_desc',
  DATE_ASCENDING = 'date_asc',
  DATE_DESCENDING = 'date_desc',
  RELEVANCE = 'relevance',
}

export enum AUTHORITY_MARC_TAGS {
  MARC_100 = 'Marc_100',
  MARC_110 = 'Marc_110',
  MARC_150 = 'Marc_150',
  MARC_151 = 'Marc_151',
}

enum WebSocketMessage {
  Results = 'results',
  Authority = 'authorityResults',
}

@Injectable()
export class SearchService implements OnDestroy {
  static readonly libraryFacetId: number = -2;

  resultType: string;
  sortBy: string;
  orderBy: string;
  private _searchResults: SearchResults;
  private _numOfRecords: number = Definitions.NUM_OF_RECORDS;
  private _userPreferenceNumOfRecords: number = undefined;
  private _destroyed$ = new Subject<void>();

  readonly JACKETART_ERROR_URL = 'https://www5.auto-graphics.com/v6assets/no-image-foundlrg.png';
  private _searchIndexesBS = new BehaviorSubject<SearchIndexes>(null);
  private _resourcesBS = new BehaviorSubject<TargetGroups[]>(null);
  private _quickSearchResourcesBS = new BehaviorSubject<TargetGroups[]>(null);
  // Search / Polling
  private _activeTargetsRemaining = new BehaviorSubject<number>(null);
  activeTargetsRemaining$ = this._activeTargetsRemaining.asObservable();
  zeroResultsTextBlock$ = new Observable<any>();
  private _webSocket: WebSocketHubConnection;

  constructor(
    private _accountService: AccountService,
    private _apiService: ApiService,
    private _configService: ConfigService,
    private _storageService: StorageService,
    private _routingService: RoutingService,
    private _sharedService: SharedService,
    private _searchHistoryService: SearchHistoryService,
    private _kidsCatalogSearchService: KidsCatalogSearchService,
    private _webSocketService: WebSocketService,
  ) {
    this._webSocket = this._webSocketService.newInstance();

    this._sharedService.getLoginStatusBS().subscribe((status) => {
      if (status == LOGIN_STATE.LOGOUT) {
        // reset "numOfRecords" to "Definitions.NUM_OF_RECORDS" on logout
        this._numOfRecords = Definitions.NUM_OF_RECORDS;
        this._userPreferenceNumOfRecords = undefined;
        this._webSocket?.stop().subscribe(() => {
          console.log('stopping search web socket');
        });
      } else if (status == LOGIN_STATE.SUCCESSFUL) {
        // connections are token based and needs to be reestablished as user changes.
        // this is required to fetch correct uxadmin settings for user. eg: facets displayed.
        this._webSocket
          .start(`${this._configService.searchApiWebSocketUrl}/search`)
          .subscribe(() => console.log('search web socket established'));
      }
    });

    this._accountService.userPreferences$.subscribe((userPreferences) => {
      if (!userPreferences) {
        return;
      }

      const searchResultPreference = userPreferences.find(
        (u) => u.name == 'DefaultSearchResultsDisplayCount',
      );
      if (searchResultPreference?.preferenceList?.length) {
        this._userPreferenceNumOfRecords = Number(
          searchResultPreference.preferenceList.find((p) => p.isChecked).value,
        );
      }
    });

    this._sharedService.userData$.subscribe((userData) => {
      if (!userData) {
        return;
      }
      this.zeroResultsTextBlock$ = this._getZeroResultsTextBlock().pipe(
        map((results) => {
          const defaultTextBlock = {
            text: '<p>No results found for the term(s) used. Please check their spelling and contact library staff for help if problems persist.</p>',
            className: 'medium-editor text-center height-250',
          };

          if (results.data.length > 0) {
            const textBlock = results.data[0].results[0];
            if (textBlock) {
              // check if HTML actually exists or if there are only html tags in there
              const text = textBlock.text;
              const innerHTMLexists = text.replace(/(<([^>]+)>)/gi, '');
              if (innerHTMLexists === '' || !innerHTMLexists) {
                return defaultTextBlock;
              } else {
                return textBlock;
              }
            }
          } else {
            // set defaults for textblock
            return defaultTextBlock;
          }
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeToWebSocket();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  static setSelectedSearchResources(resources: TargetGroups[], selectedResources: number[]) {
    // set selected resource based on selected libraryPoolKey
    resources?.forEach((r) => {
      r.searchTargets.forEach((t) => {
        t.isDefault = selectedResources.includes(t.libraryDBpoolKey);
      });
    });

    SearchService.setCheckStatus(resources);
  }

  static setCheckStatus(resources?: TargetGroups[]) {
    // check make the top group checkbox as true if all the searchGroups targets are true
    // first check if no targets are false, then all are selected
    // if all are not selected, then if there any select, then the group is partially selected
    resources?.forEach((r) => {
      r.isDefault = r.searchTargets.find((t) => !t.isDefault) ? false : true;
      if (r.isDefault) {
        r.checkedStatus = CHECKED_STATUS.ALL;
      } else {
        r.checkedStatus = r.searchTargets.find((t) => t.isDefault)
          ? CHECKED_STATUS.PARTIAL
          : CHECKED_STATUS.NONE;
      }
    });
  }

  static toggleResourcesByLibraryDBPoolKey(resources: TargetGroups[], resource: SearchTarget) {
    resources.forEach((r) =>
      r.searchTargets.forEach((s) => {
        if (s.libraryDBpoolKey === resource.libraryDBpoolKey) {
          s.isDefault = resource.isDefault;
        }
      }),
    );

    SearchService.setCheckStatus(resources);
  }

  static getSelectResources(resources: TargetGroups[]): number[] {
    const selectedResources: number[] = [];
    resources?.forEach((r) => {
      r.searchTargets.forEach((t) => {
        if (t.isDefault) {
          selectedResources.push(t.libraryDBpoolKey);
        }
      });
    });

    return selectedResources;
  }

  unsubscribeToWebSocket() {
    this._webSocket.off(WebSocketMessage.Authority);
    this._webSocket.off(WebSocketMessage.Results);
  }

  authoritySearchWebSocket(authorityRequest, callback: () => void) {
    this._webSocket.off(WebSocketMessage.Authority);
    this._webSocket.on(WebSocketMessage.Authority, callback);

    this._webSocket.invoke(WebSocketMessage.Authority, authorityRequest).subscribe(() => {
      console.log('performing auth search');
    });
  }

  pollResultsWebSocket(pollRequest, callback: () => void) {
    this._webSocket.off(WebSocketMessage.Results);
    this._webSocket.on(WebSocketMessage.Results, callback);

    this._webSocket.invoke(WebSocketMessage.Results, pollRequest).subscribe(() => {
      console.log('performing search');
    });
  }

  private _getZeroResultsTextBlock(): Observable<any> {
    const url =
      this._configService.expressApi +
      '/zeroResults' +
      '/' +
      this._storageService.getCid() +
      '/' +
      this._storageService.getLid();
    return this._apiService.get(url);
  }

  saveZeroResultsTextBlock(data): Observable<any> {
    const url = this._configService.expressApi + '/zeroResults';
    return this._apiService.post(url, data);
  }

  getSearchIndexesBS(): BehaviorSubject<SearchIndexes> {
    return this._searchIndexesBS;
  }

  getSearchResourcesBS(): BehaviorSubject<TargetGroups[]> {
    return this._resourcesBS;
  }

  private _setSearchId(searchId: string) {
    this._storageService.setItem(Definitions.searchIdSessionKey, searchId);
  }

  getSearchId(): string {
    return this._storageService.getItem(Definitions.searchIdSessionKey);
  }

  private _setSearchResources(value: TargetGroups[]) {
    this._storageService.setItem('availableResources', value);
  }

  getSearchResources(): TargetGroups[] {
    return this._storageService.getItem('availableResources');
  }

  getQuickSearchResourcesBS(): BehaviorSubject<TargetGroups[]> {
    return this._quickSearchResourcesBS;
  }

  setQuickSearchResourcesBS(targetGroups: TargetGroups[]): void {
    return this._quickSearchResourcesBS.next(targetGroups);
  }

  setActiveTargetsRemaining(remaining: number) {
    this._activeTargetsRemaining.next(remaining);
  }

  set numOfRecords(value: number) {
    this._numOfRecords = value;
  }

  get numOfRecords(): number {
    if (this._userPreferenceNumOfRecords) {
      this._numOfRecords = this._userPreferenceNumOfRecords;
      this._userPreferenceNumOfRecords = undefined;
    }
    return this._numOfRecords;
  }

  doSearchSimple(index: string, query: string, resources: number[], scopingLevel: number): void {
    const searchPayload: SearchRequestPayload = {
      searchQuery: {
        matches: [
          {
            query: query,
            index: index,
            queryOption: Definitions.QUERY_OPTION,
            matchJoinOperator: Definitions.MATCH_JOIN_OPERATOR,
          },
        ],
        filters: {
          scopingLevel: scopingLevel,
        },
        facetMatches: [],
        resources: resources,
        startRecord: Definitions.START_RECORD,
        numOfRecords: this.numOfRecords,
        sortBy: [this.sortBy],
        orderBy: this.orderBy,
        resultType: this.resultType,
        searchType: SEARCH_TYPE.SIMPLE,
      },
    };
    this.doSearch(searchPayload);
  }

  doSearch(searchPayload: SearchRequestPayload, isDidYouMeanSearch: boolean = false) {
    if (!searchPayload.searchQuery.resources?.length) {
      this._sharedService.openSnackBar('Please select resources for search', 'ok');
      return;
    }

    searchPayload.searchQuery.isKidsSearch = this._sharedService.getIsKidsCat();

    if (!isDidYouMeanSearch) {
      this._searchHistoryService.setHistoryCached(searchPayload); // used by modify search
      const matches = searchPayload?.searchQuery?.matches || [];
      const searchTerm = matches.map((match) => match.query).join(', ');
      this._storageService.setSearchTerm(searchTerm);
    }
    this.performSearch(searchPayload).subscribe(
      (data) => {
        this._setSearchId(data.searchId);
        this.goToSearch(data.searchId, isDidYouMeanSearch);
      },
      (error: unknown) => {
        const message = 'Unable to perform search.';
        this._sharedService.openSnackBar(message);
        if (error instanceof Error) {
          console.error(`${message}:${error.message}`);
        }
      },
    );
  }

  performSearch(searchRequestPayload: SearchRequestPayload): Observable<SearchStatus> {
    // old "performSearch" just returned Observable returned .post
    const url = `${this._configService.searchApi}/search`;
    return this._apiService.post(url, searchRequestPayload).pipe(
      map((response: ApiResponse<SearchStatus>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to do search.');
        }
      }),
    );
  }

  performSearchAndWait(searchRequestPayload: SearchRequestPayload): Observable<SearchResults> {
    const url = `${this._configService.searchApi}/search/searchAndWait`;
    return this._apiService.post(url, searchRequestPayload).pipe(
      map((response: ApiResponse<SearchResults>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to perform search');
        }
      }),
    );
  }

  pollResults(data: PollResultsRequestPayload): Observable<SearchResults> {
    const url = `${this._configService.searchApi}/search/results`;
    return this._apiService.post(url, data).pipe(
      map((response: ApiResponse<SearchResults>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to fetch search results');
        }
      }),
    );
  }

  moreSearchResults(searchRequestPayload: SearchRequestPayload): Observable<SearchResults> {
    const url = `${this._configService.searchApi}/search/moreSearchResults`;
    return this._apiService.post(url, searchRequestPayload).pipe(
      map((response: ApiResponse<SearchResults>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to fetch more search results');
        }
      }),
    );
  }

  facetsResults(searchRequestPayload: SearchRequestPayload): Observable<SearchResults> {
    const url = `${this._configService.searchApi}/search/facet`;
    return this._apiService.post(url, searchRequestPayload).pipe(
      map((response: ApiResponse<SearchResults>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to fetch facet results.');
        }
      }),
    );
  }

  suggestSearchResults(query: string): Observable<TypeAhead[]> {
    const url = `${this._configService.searchApi}/search/suggest?query=${query}`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<TypeAhead[]>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to fetch type ahead suggestions.');
        }
      }),
    );
  }

  goToSearch(searchId: string, isDidYouMeanSearch: boolean): void {
    this._routingService.goToSearch(searchId, isDidYouMeanSearch);
  }

  getAvailableTargets(): Observable<ApiResponse<AvailableTargetsInfo>> {
    const url = `${this._configService.searchApi}/library/AvailableTargets`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<AvailableTargetsInfo>) => {
        SearchService.setCheckStatus(response.data.targetGroups);

        this._setSearchResources(response.data.targetGroups);
        this._resourcesBS.next(response.data.targetGroups);

        this._quickSearchResourcesBS.next(response.data.targetGroups);

        return response;
      }),
    );
  }

  getSearchIndexes(): Observable<SearchIndexes> {
    const url = this._configService.agApi + '/opac/searchindex';
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<SearchIndexes>) => {
        const searchIndexes: SearchIndexes = {
          simpleSearchIndexes: response.data.simpleSearchIndexes,
          advancedSearchIndexes: response.data.advancedSearchIndexes,
        };
        this._searchIndexesBS.next(searchIndexes);

        return searchIndexes;
      }),
    );
  }

  getBrowseSearchResults(
    index: string,
    searchTerm: string,
    pageSize: number,
  ): Observable<BrowseSearchData> {
    const url = `${this._configService.searchApi}/browse/search/${index}/${searchTerm}/?pageSize=${pageSize}`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<BrowseSearchData>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to get browse search offset.');
        }
      }),
    );
  }

  getBrowseSearchOffset(
    index: string,
    offset: string,
    pageSize: number,
  ): Observable<BrowseSearchData> {
    const url = `${this._configService.searchApi}/browse/${index}/${offset}/?pageSize=${pageSize}`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<BrowseSearchData>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error('unable to get browse search results.');
        }
      }),
    );
  }

  goToBrief(record, searchId, format, url, agControlId): void {
    this._storageService.setBriefRecordCache(record.formats);
    this._routingService.pushOnBackStack(url);
    this._routingService.goToBrief(searchId, format, agControlId);
  }

  getSortAndOrderBy(sortByValue) {
    let sort;
    let order;
    switch (sortByValue) {
      case SORTBYTYPES.TITLE_ASCENDING:
        sort = 'title';
        order = 'asc';
        break;
      case SORTBYTYPES.TITLE_DESCENDING:
        sort = 'title';
        order = 'desc';
        break;
      case SORTBYTYPES.AUTHOR_ASCENDING:
        sort = 'author';
        order = 'asc';
        break;
      case SORTBYTYPES.AUTHOR_DESCENDING:
        sort = 'author';
        order = 'desc';
        break;
      case SORTBYTYPES.DATE_ASCENDING:
        sort = 'date';
        order = 'asc';
        break;
      case SORTBYTYPES.DATE_DESCENDING:
        sort = 'date';
        order = 'desc';
        break;
      case SORTBYTYPES.RELEVANCE:
      default:
        sort = 'relevance';
        order = 'asc';
    }
    return {
      sortBy: sort,
      orderBy: order,
    };
  }

  getSortByType(sortBy, orderBy): string {
    if (Array.isArray(sortBy)) {
      sortBy = sortBy[0];
    }

    const sortByOrderBy = `${sortBy}_${orderBy}`?.toLowerCase();
    switch (sortByOrderBy) {
      case SORTBYTYPES.TITLE_ASCENDING:
      case SORTBYTYPES.TITLE_DESCENDING:
      case SORTBYTYPES.AUTHOR_ASCENDING:
      case SORTBYTYPES.AUTHOR_DESCENDING:
      case SORTBYTYPES.DATE_ASCENDING:
      case SORTBYTYPES.DATE_DESCENDING:
        return sortByOrderBy;
      default:
        return SORTBYTYPES.RELEVANCE;
    }
  }

  setSearchResults(searchResults: SearchResults): void {
    this._searchResults = searchResults;
  }

  private _getPreviousAgControlId(currentAgControlId: string): Observable<Document> {
    const allDocuments = this._searchResults.results
      .flatMap((r) => r.formats)
      .flatMap((f) => f.documents);
    let indexOfCurrent = allDocuments.findIndex((d) => d.agControlId == currentAgControlId);
    const previousIndex = --indexOfCurrent;

    //return prev document
    if (previousIndex > -1) {
      const doc = allDocuments[previousIndex];
      return of(doc);

      //are no prev pages
    } else if (this._searchResults.searchQuery.startRecord == 1) {
      return of(null);

      //need to fetch prev page
    } else {
      let prevStartRecord =
        this._searchResults.searchQuery.startRecord - this._searchResults.searchQuery.numOfRecords;
      if (prevStartRecord < 1) {
        prevStartRecord = 1;
      }

      return this.pollResults({
        searchId: this._searchResults.searchId,
        startRecord: prevStartRecord,
      }).pipe(
        switchMap((response) => {
          this.setSearchResults(response);
          const allDocuments = this._searchResults.results
            .flatMap((r) => r.formats)
            .flatMap((f) => f.documents);
          return of(allDocuments[allDocuments.length - 1]);
        }),
      );
    }
  }

  private _getNextAgControlId(currentAgControlId: string): Observable<Document> {
    const allDocuments = this._searchResults.results
      .flatMap((r) => r.formats)
      .flatMap((f) => f.documents);
    let indexOfCurrent = allDocuments.findIndex((d) => d.agControlId == currentAgControlId);
    const nextIndex = ++indexOfCurrent;

    //return next document
    if (nextIndex < allDocuments.length) {
      const doc = allDocuments[nextIndex];
      return of(doc);

      //need to fetch next page
    } else {
      const nextStartRecord =
        this._searchResults.searchQuery.startRecord + this._searchResults.searchQuery.numOfRecords;

      return this.pollResults({
        searchId: this._searchResults.searchId,
        startRecord: nextStartRecord,
      }).pipe(
        switchMap((response) => {
          //no more pages
          if (response.results?.length == 0) {
            return of(null);
          }

          this.setSearchResults(response);
          const allDocuments = this._searchResults.results
            .flatMap((r) => r.formats)
            .flatMap((f) => f.documents);
          return of(allDocuments[0]);
        }),
      );
    }
  }

  nextAgControlId(searchId: string, currentAgcn: string): Observable<Document> {
    if (this._searchResults && this._searchResults.searchId == searchId) {
      return this._getNextAgControlId(currentAgcn);
    } else {
      return this.pollResults({ searchId: searchId }).pipe(
        switchMap((response) => {
          this.setSearchResults(response);
          return this._getNextAgControlId(currentAgcn);
        }),
      );
    }
  }

  hasNextAgControlId(searchId: string, currentAgcn: string): Observable<boolean> {
    if (this._searchResults?.searchId == searchId) {
      const formats = this._searchResults.results.flatMap((r) => r.formats);
      const allDocuments = formats.flatMap((f) => f.documents);
      const indexOfCurrent = allDocuments.findIndex((d) => d.agControlId == currentAgcn);
      const isLastDocument = indexOfCurrent == allDocuments.length - 1;
      const thereAreNoMoreResults = this._searchResults.searchQuery.numOfRecords > formats.length;
      return of(!(isLastDocument && thereAreNoMoreResults));
    } else {
      return this.pollResults({ searchId: searchId }).pipe(
        switchMap((response) => {
          this.setSearchResults(response);
          if (response?.searchId == searchId) {
            return this.hasNextAgControlId(searchId, currentAgcn);
          }
        }),
      );
    }
  }

  hasPreviousAgControlId(searchId: string, currentAgcn: string): Observable<boolean> {
    if (this._searchResults?.searchId == searchId) {
      const allDocuments = this._searchResults.results
        .flatMap((r) => r.formats)
        .flatMap((f) => f.documents);
      const indexOfCurrent = allDocuments.findIndex((d) => d.agControlId == currentAgcn);
      return of(!(!indexOfCurrent && this._searchResults.searchQuery.startRecord == 1));
    } else {
      return this.pollResults({ searchId: searchId }).pipe(
        switchMap((response) => {
          this.setSearchResults(response);
          if (response?.searchId == searchId) {
            return this.hasPreviousAgControlId(searchId, currentAgcn);
          }
        }),
      );
    }
  }

  previousAgControlId(searchId: string, currentAgcn: string): Observable<Document> {
    if (this._searchResults && this._searchResults.searchId == searchId) {
      return this._getPreviousAgControlId(currentAgcn);
    } else {
      return this.pollResults({ searchId: searchId }).pipe(
        switchMap((response) => {
          this.setSearchResults(response);
          return this._getPreviousAgControlId(currentAgcn);
        }),
      );
    }
  }

  async search(query: SearchQuery) {
    // loop the query matches here to get the total results for string on search page
    // save the query in search-term object in session to retain the term on search
    const array = query.matches ?? [];
    const queryString = array.map((element) => element.query).join(', ');
    this._storageService.setItem(Definitions.searchTermSessionKey, queryString);
    this._removeSearchResultsScrolling();

    if (this._sharedService.getIsKidsCat()) {
      query = this._updateSearchBodyForKids(query);
      this._kidsCatalogSearchService.setKidsCacheSearchRequest({
        searchQuery: this._buildKidsCacheBody(query.matches),
      });
    }

    const postBody: SearchRequestPayload = {
      searchQuery: query,
    };
    // TODO search.component should be refactored to use values passed in query params
    // instead of referencing values in storage, not all query params are currently used
    // only startRecord and searchId
    this._sharedService.getIsKidsCat()
      ? this._kidsCatalogSearchService.setKidsCacheSearchRequest({
          searchQuery: this._buildKidsCacheBody(query.matches),
        })
      : this._searchHistoryService.setHistoryCached(postBody);

    this.performSearch(postBody).subscribe((response) => {
      const searchId = response.searchId;
      const queryParams = {
        searchId: searchId,
        startRecord: query.startRecord,
      };
      this._routingService.goToSearchWithQueryParams(queryParams);
    });
  }

  private _removeSearchResultsScrolling() {
    this._storageService.remove(Definitions.SEARCHRESULTS_YOFFSET);
    this._storageService.remove(Definitions.SEARCHRESULTS_FULLRECORD_AGCONTROLID);
  }

  private _buildKidsCacheBody(matches): SearchQuery {
    return {
      matches: matches,
      numOfFacetPerGroup: 20,
      numOfRecords: 40,
      orderBy: 'asc',
      sortBy: ['relevance'],
      startRecord: 1,
      isKidsSearch: true,
    };
  }

  private _updateSearchBodyForKids(searchQuery: SearchQuery): SearchQuery {
    // Update the searchQuery object directly with new values for the kids search
    searchQuery.numOfFacetPerGroup = 20;
    searchQuery.numOfRecords = 40;
    searchQuery.orderBy = 'asc';
    searchQuery.sortBy = ['relevance'];
    searchQuery.startRecord = 1;
    searchQuery.isKidsSearch = true;

    // Return the updated searchQuery object
    return searchQuery;
  }
}
